import React, { useMemo, useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import ZionForm, { useZionForm, FormTextField, FormCheckbox, FormSelect, useWatch } from '@fs/zion-form'
import {
  InfoSheetPanel,
  BillboardButton,
  Button,
  DataBlock,
  Expander,
  ExpanderContent,
  ExpanderHeader,
  H3,
  Row,
  Separator,
  usePageWidth,
} from '@fs/zion-ui'
import { ContentAdd, ContentDelete } from '@fs/zion-icon'
import { getElementTypeDisplayLabel, getNextUuid, useSlsHighlightsContext, useSlsStateContext } from '@fs/zion-sls'
import HighlightButton from '../highlightButton/HighlightButton'
import { useFamilyReviewStateContext, useFamilyReviewDispatchContext } from '../../providers/FamilyReviewContext'
import { stepsMap } from '../../providers/FamilyReviewReducer'
import CustomFormError from './CustomFormError'

const CreateNewPersonFormPage1 = ({
  createNewPersonFormPage1Overlay,
  currentTask,
  handleSubmit,
  hasSelectedHighlights,
  editHighlightsOverlay,

  headingColor = 'green',
  isPrimaryPerson = false,
}) => {
  const h = useSlsHighlightsContext()
  const [t] = useTranslation()
  const atPageWidth = usePageWidth()
  const isSmallScreen = atPageWidth({ default: true, md: false })
  const zionSlsState = useSlsStateContext()
  const state = useFamilyReviewStateContext()
  const dispatch = useFamilyReviewDispatchContext()
  const [givenNameFieldObj, setGivenNameFieldObj] = useState()
  const [surnameFieldObj, setSurnameFieldObj] = useState()
  const [givenNameFieldId, setGivenNameFieldId] = useState(getNextUuid())
  const [surnameFieldId, setSurnameFieldId] = useState(getNextUuid())
  const submitButtonClicked = useRef(false)

  const hasHighlight = (fieldId) => {
    return [...h.getHighlights().mainEditHighlights, ...h.getHighlights().splitScreenEditHighlights].find(
      (highlight) => highlight.fieldId === fieldId
    )
  }

  const methods = useZionForm({
    mode: 'all',
    defaultValues: {
      nameType: 'BIRTH_NAME',
      givenField: '',
      surnameField: '',
      givenNoHighlightCheckbox: '',
      surnameNoHighlightCheckbox: '',
    },
    resolver: (values) => {
      const errors = {}
      methods.clearErrors()
      const { givenNoHighlightCheckbox, surnameNoHighlightCheckbox, givenField, surnameField, nameType } = values
      if (!nameType)
        errors.nameType = {
          type: 'required',
          message: t('add-person.form-error-one', 'Must have a name type'),
        }
      if (!givenField && !surnameField) {
        errors.givenField = {
          type: 'required',
          message: t('add-person.form-error-three', 'Must have a given name or a surname'),
        }
        errors.surnameField = {
          type: 'required',
          message: t('add-person.form-error-three', 'Must have a given name or a surname'),
        }
      }
      if (submitButtonClicked.current) {
        if (givenField && !givenNoHighlightCheckbox && !hasHighlight(givenNameFieldId))
          errors.givenField = {
            type: 'required',
            message: t('add-person.form-error-two', 'Must either add a highlight or select No Highlight Possible'),
          }
        if (surnameField && !surnameNoHighlightCheckbox && !hasHighlight(surnameFieldId))
          errors.surnameField = {
            type: 'required',
            message: t('add-person.form-error-two', 'Must either add a highlight or select No Highlight Possible'),
          }
        submitButtonClicked.current = false
      }
      return { values, errors }
    },
  })

  const [givenNoHighlightCheckbox, surnameNoHighlightCheckbox] = useWatch({
    name: ['givenNoHighlightCheckbox', 'surnameNoHighlightCheckbox'],
    control: methods.control,
  })

  const givenDisplayLabel = getElementTypeDisplayLabel(
    { elementType: 'FIELD', fieldType: 'NAME_GN' },
    zionSlsState?.types,
    t
  )
  const surnameDisplayLabel = getElementTypeDisplayLabel(
    { elementType: 'FIELD', fieldType: 'NAME_SURN' },
    zionSlsState?.types,
    t
  )

  const nameTypeSelectOptions = useMemo(
    () =>
      zionSlsState.types?.names?.map((name) => {
        return {
          value: name.nameType,
          label: name.displayLabel,
        }
      }),
    [zionSlsState.types?.names]
  )

  useEffect(() => {
    if (createNewPersonFormPage1Overlay.isOpen) {
      methods.reset({
        nameType: 'BIRTH_NAME',
        givenField: '',
        surnameField: '',
        givenNoHighlightCheckbox: false,
        surnameNoHighlightCheckbox: false,
      }) // Reset this whenever overlay opens
      setGivenNameFieldId(getNextUuid())
      setSurnameFieldId(getNextUuid())
      setGivenNameFieldObj(null)
      setSurnameFieldObj(null)
      h.clearDisplay({ all: true })
    }
    // Adding h as a dependency causes the funciton, h.clearDisplay, to be called at the wrong times. eye-roll for useEffect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createNewPersonFormPage1Overlay.isOpen, methods, isSmallScreen])

  const handleHighlightButtonClicked = (id, fieldData, formFieldName) => {
    if (id) dispatch({ type: 'SET_ACTIVE_FIELD_ID', value: id })
    if (fieldData && formFieldName)
      h.setActiveFieldInfo({ ...fieldData, value: methods.getValues(formFieldName) || '' })

    h.setDisplayHighlights({
      mainPartitionKey: currentTask.partitionKey,
      splitScreenPartitionKey: state.splitScreenPartitionKey,
      setMainZoom: true,
      setSplitScreenZoom: Boolean(state.splitScreenPartitionKey),
      attachFilter: (hl) => hl.fieldId === id,
      mainZoomFilter: (hl) => hl.fieldId === id,
      splitScreenZoomFilter: (hl) => hl.fieldId === id,
      isSmallScreen,
    })
  }

  const handleCloseCreateNewPersonFormPage1 = () => {
    createNewPersonFormPage1Overlay.close()
    h.clearEdits()
    h.clearDisplay({ all: true })
  }

  const cancelButtonClicked = () => {
    handleCloseCreateNewPersonFormPage1()
  }

  const nextButtonClicked = async () => {
    submitButtonClicked.current = true
    const isValid = await methods.trigger()
    if (isValid) handleSubmit(methods.getValues(), givenNameFieldId, surnameFieldId)
  }

  const userClosedPanel = () => {
    handleCloseCreateNewPersonFormPage1()
  }
  const isMissingGivenOrSurname =
    Boolean(methods.formState.errors?.givenField || methods.formState.errors?.surnameField) &&
    Boolean(!surnameFieldObj && !givenNameFieldObj)
  return (
    <InfoSheetPanel
      {...{ ...createNewPersonFormPage1Overlay, close: userClosedPanel }}
      headingColor={headingColor}
      title={
        state.currentStep === stepsMap.STEP_ONE
          ? t('family-review.step1.heading', 'Identify Primary Person')
          : t('family-review.step3.heading', 'Family Relationships')
      }
      footer={
        <Row alignX="end">
          <Button
            onClick={cancelButtonClicked}
            data-testid="add-person-create-form-cancel-button"
            linkName="Create New Person One Cancel"
          >
            {t('navigation.cancel-button', 'Cancel')}
          </Button>
          <Button
            emphasis="high"
            data-testid="add-person-create-form-next-button"
            type="submit"
            form="create-new-person-form-1"
            linkName={`Add Person ${isPrimaryPerson ? 'Save' : 'Next'}`}
          >
            {isPrimaryPerson ? t('navigation.save-button', 'Save') : t('navigation.next-button', 'Next')}
          </Button>
        </Row>
      }
    >
      {createNewPersonFormPage1Overlay.isOpen && (
        <>
          <H3 size="H5">
            {isPrimaryPerson
              ? t('family-review.addPerson.add-new-primary', 'Add New Primary Person')
              : t('family-review.addPerson.add-new', 'Add new Person')}
          </H3>

          <DataBlock
            heading={t('add-person.subtitle', 'Select and fill out a name part to continue.')}
            data={t('add-person.body-text', 'Must have at least one name part and a highlight.')}
          />
          <Separator size="xs" />
          <ZionForm methods={methods} onSubmit={nextButtonClicked} id="create-new-person-form-1">
            <FormSelect
              name="nameType"
              label={t('add-person.name-type-select', 'Name Type')}
              placeholder={t('add-person.name-type-select', 'Name Type')}
              options={nameTypeSelectOptions}
            />
            {!givenNameFieldObj && (
              <Row>
                <BillboardButton
                  data-testid="add-given-name"
                  Icon={ContentAdd}
                  color="blue"
                  emphasis="low"
                  linkName="Add Person Add Given Name"
                  onClick={() =>
                    setGivenNameFieldObj({
                      id: givenNameFieldId,
                      label: givenDisplayLabel,
                      fieldType: 'NAME_GN',
                      name: 'givenField',
                    })
                  }
                >
                  {t('add-person.add-given-name', 'Add given name')}
                </BillboardButton>
              </Row>
            )}
            {givenNameFieldObj && (
              <Expander initiallyOpen>
                <ExpanderHeader>{givenDisplayLabel}</ExpanderHeader>
                <ExpanderContent>
                  <FormTextField
                    data-testid="addPersonNameField0"
                    name="givenField"
                    label={givenDisplayLabel}
                    placeholder={givenDisplayLabel}
                    maxLength={256}
                  />
                  <HighlightButton
                    handleHighlightButtonClicked={(id) => {
                      handleHighlightButtonClicked(id, givenNameFieldObj, 'givenField')
                    }}
                    disabled={givenNoHighlightCheckbox}
                    editHighlightsOverlay={editHighlightsOverlay}
                    id={givenNameFieldId}
                    isAdding={!hasSelectedHighlights(givenNameFieldId)}
                  >
                    {t('add-person.highlight-button', 'Add Highlight')}
                  </HighlightButton>
                  <FormCheckbox
                    data-testid="addPersonNoHighlightPossible0"
                    label={t('highlight-required-checkbox-label', 'No highlight possible')}
                    name="givenNoHighlightCheckbox"
                    disabled={Boolean(hasSelectedHighlights(givenNameFieldId))}
                  />
                  <BillboardButton
                    data-testid="delete-given-name"
                    color="red"
                    Icon={ContentDelete}
                    emphasis="low"
                    linkName="Add Person Remove Given Name"
                    onClick={() => {
                      setGivenNameFieldObj(null)
                      methods.setValue('givenField', '')
                    }}
                  >
                    {t('add-person.remove-given-name', 'Remove given name')}
                  </BillboardButton>
                </ExpanderContent>
              </Expander>
            )}

            {!surnameFieldObj && (
              <Row>
                <BillboardButton
                  data-testid="add-surname"
                  Icon={ContentAdd}
                  color="blue"
                  emphasis="low"
                  linkName="Add Person Add Surname"
                  onClick={() =>
                    setSurnameFieldObj({
                      id: surnameFieldId,
                      label: surnameDisplayLabel,
                      fieldType: 'NAME_SURN',
                      newPersonId: '',
                      name: 'surnameField',
                    })
                  }
                >
                  {t('add-person.add-surname', 'Add surname')}
                </BillboardButton>
              </Row>
            )}
            {isMissingGivenOrSurname && (
              <CustomFormError message={t('add-person.form-error-three', 'Must have a given name or a surname')} />
            )}
            {surnameFieldObj && (
              <Expander initiallyOpen>
                <ExpanderHeader>{surnameDisplayLabel}</ExpanderHeader>
                <ExpanderContent>
                  <FormTextField
                    data-testid="addPersonNameField1"
                    name="surnameField"
                    label={surnameDisplayLabel}
                    placeholder={surnameDisplayLabel}
                    maxLength={256}
                  />
                  <HighlightButton
                    handleHighlightButtonClicked={(id) => {
                      handleHighlightButtonClicked(id, surnameFieldObj, 'surnameField')
                    }}
                    disabled={surnameNoHighlightCheckbox}
                    editHighlightsOverlay={editHighlightsOverlay}
                    id={surnameFieldId}
                    isAdding={!hasSelectedHighlights(surnameFieldId)}
                  >
                    {t('add-person.highlight-button', 'Add Highlight')}
                  </HighlightButton>
                  <FormCheckbox
                    data-testid="addPersonNoHighlightPossible1"
                    label={t('highlight-required-checkbox-label', 'No highlight possible')}
                    name="surnameNoHighlightCheckbox"
                    disabled={Boolean(hasSelectedHighlights(surnameFieldId))}
                  />
                  <BillboardButton
                    data-testid="delete-surname"
                    color="red"
                    Icon={ContentDelete}
                    emphasis="low"
                    linkName="Add Person Remove Surname"
                    onClick={() => {
                      setSurnameFieldObj(null)
                      methods.setValue('surnameField', '')
                    }}
                  >
                    {t('add-person.remove-surname', 'Remove surname')}
                  </BillboardButton>
                </ExpanderContent>
              </Expander>
            )}
          </ZionForm>
        </>
      )}
    </InfoSheetPanel>
  )
}

export default CreateNewPersonFormPage1
